/**
 * The scenario lab: "if we concede X, who moves, and who do we need on side
 * first?"
 *
 * The analyst builds levers, the projection follows as they think (previewed,
 * debounced), and named scenarios can be saved and set against each other.
 * Nothing here is presented as a forecast — every number is traceable to a
 * lever they wrote and a propagation assumption they chose.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, FlaskConical, Play, Plus, Save, ServerCrash, Trash2, Users } from 'lucide-react';
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  Pill,
  SectionHeader,
  Skeleton,
  Spinner,
  Textarea,
} from '../../../components/ui';
import { useReducedMotion } from '../../intelligence/network/ui';
import type { IssueView, ScenarioProjection, ScenarioView } from '../../../types/issues';
import {
  IssuesUnavailableError,
  createScenario,
  deleteScenario,
  issuesErrorMessage,
  listScenarios,
  previewScenario,
  runScenario,
  updateScenario,
} from '../issuesApi';
import { useIssueMap } from '../map/useIssueMap';
import { formatIndex } from '../map/position';
import { LeverList } from './LeverEditor';
import { ProjectionView } from './ProjectionView';
import {
  BLANK_DRAFT,
  ScenarioDraft,
  activeLevers,
  draftFrom,
  draftSignature,
  isDirty,
  isProjectable,
  propagationWords,
  summarise,
  toInput,
} from './scenarioModel';

const PREVIEW_DELAY = 450;

export const ScenarioLab: React.FC<{ issue: IssueView }> = ({ issue }) => {
  const { model, loading: mapLoading } = useIssueMap(issue.id);
  const reducedMotion = useReducedMotion();

  const [scenarios, setScenarios] = useState<ScenarioView[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<ScenarioDraft>(BLANK_DRAFT);
  const [projection, setProjection] = useState<ScenarioProjection | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [comparing, setComparing] = useState(false);

  const saved = useMemo(() => scenarios.find((s) => s.id === draft.id) || null, [scenarios, draft.id]);
  const dirty = isDirty(draft, saved);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listScenarios(issue.id);
      setScenarios(list);
      setUnavailable(false);
      setError(null);
    } catch (err) {
      const missing = err instanceof IssuesUnavailableError || (err as any)?.response?.status === 404;
      setUnavailable(missing);
      setError(missing ? 'This server does not have scenarios yet' : issuesErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [issue.id]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- preview as they think ---------- */
  const signature = draftSignature(draft);
  const request = useRef(0);
  useEffect(() => {
    if (!isProjectable(draft)) {
      setProjection(null);
      setPreviewing(false);
      return;
    }
    const ticket = ++request.current;
    const timer = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const next = await previewScenario(issue.id, toInput(draft));
        if (ticket === request.current) {
          setProjection(next);
          setError(null);
        }
      } catch (err) {
        if (ticket === request.current) setError(issuesErrorMessage(err));
      } finally {
        if (ticket === request.current) setPreviewing(false);
      }
    }, PREVIEW_DELAY);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, issue.id]);

  const open = (scenario: ScenarioView | null) => {
    setDraft(scenario ? draftFrom(scenario) : { ...BLANK_DRAFT, levers: [] });
    setProjection(scenario?.result ?? null);
    setComparing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const input = toInput(draft);
      const result = draft.id ? await updateScenario(issue.id, draft.id, input) : await createScenario(issue.id, input);
      setScenarios((list) => (draft.id ? list.map((s) => (s.id === result.id ? result : s)) : [result, ...list]));
      setDraft(draftFrom(result));
      setError(null);
      return result;
    } catch (err) {
      setError(issuesErrorMessage(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  /** Running stores the projection against the scenario, so it can be compared later */
  const run = async () => {
    const target = dirty || !draft.id ? await save() : saved;
    if (!target) return;
    setSaving(true);
    try {
      const result = await runScenario(issue.id, target.id);
      setProjection(result);
      setScenarios((list) => list.map((s) => (s.id === target.id ? { ...s, result, resultAt: result.computedAt } : s)));
      setError(null);
    } catch (err) {
      setError(issuesErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.id) return;
    const id = draft.id;
    setConfirmDelete(false);
    try {
      await deleteScenario(issue.id, id);
      setScenarios((list) => list.filter((s) => s.id !== id));
      open(null);
    } catch (err) {
      setError(issuesErrorMessage(err));
    }
  };

  const patch = (changes: Partial<ScenarioDraft>) => setDraft((current) => ({ ...current, ...changes }));

  if (loading || mapLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
        <Card padded>
          <Skeleton className="h-4 w-32" />
          <Skeleton className="mt-3 h-9 w-full" />
          <Skeleton className="mt-2 h-20 w-full" />
        </Card>
        <Card padded>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-3 h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (unavailable) {
    return (
      <Card>
        <EmptyState
          icon={ServerCrash}
          title={error || 'Scenarios are not available on this server yet'}
          action={
            <Button size="sm" variant="secondary" onClick={load}>
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  if (model.nodes.length === 0) {
    return (
      <Card>
        <EmptyState icon={Users} title="No positions recorded yet — a scenario needs stances to move" />
      </Card>
    );
  }

  const comparable = scenarios.filter((s) => s.result);
  const summary = summarise(projection);

  return (
    <div className="space-y-4">
      {/* ---------- saved scenarios ---------- */}
      <Card padded>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionHeader
            title="Scenarios"
            aside={<span>{scenarios.length ? `${scenarios.length} saved` : 'None saved yet'}</span>}
            className="mb-0"
          />
          <div className="flex items-center gap-2">
            {comparable.length > 1 && (
              <Button size="sm" variant={comparing ? 'dark' : 'secondary'} onClick={() => setComparing((v) => !v)}>
                <BarChart3 className="h-4 w-4" strokeWidth={1.75} />
                Compare
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => open(null)}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              New
            </Button>
          </div>
        </div>

        {scenarios.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {scenarios.map((scenario) => {
              const active = scenario.id === draft.id;
              const delta = scenario.result ? (scenario.result.after?.index ?? 0) - (scenario.result.before?.index ?? 0) : null;
              return (
                <button
                  key={scenario.id}
                  type="button"
                  onClick={() => open(scenario)}
                  className={`inline-flex h-8 max-w-full items-center gap-2 rounded-full px-3 text-[12.5px] transition-colors ${
                    active ? 'bg-ink text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  <span className="truncate">{scenario.name}</span>
                  {delta !== null && (
                    <span className={`tabular-nums ${active ? 'text-white/70' : 'text-neutral-400'}`}>{formatIndex(delta)}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {comparing && comparable.length > 1 ? (
        <Card divided>
          <div className="px-4 pb-1 pt-3.5">
            <SectionHeader
              title="Side by side"
              aside={<span className="hidden sm:inline">Only scenarios that have been run</span>}
              className="mb-0"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-[13px]">
              <thead>
                <tr className="text-left text-[11.5px] text-neutral-500">
                  <th className="px-4 py-2 font-normal">Scenario</th>
                  <th className="px-4 py-2 font-normal">Levers</th>
                  <th className="px-4 py-2 text-right font-normal">Balance</th>
                  <th className="px-4 py-2 text-right font-normal">Change</th>
                  <th className="px-4 py-2 text-right font-normal">Flips</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200/70">
                {comparable.map((scenario) => {
                  const stats = summarise(scenario.result);
                  return (
                    <tr key={scenario.id} className={scenario.id === draft.id ? 'bg-neutral-50' : ''}>
                      <td className="px-4 py-2.5">
                        <button type="button" onClick={() => open(scenario)} className="text-left text-ink hover:text-primary-600">
                          {scenario.name}
                        </button>
                        {scenario.levers.some((l) => l.cost) && (
                          <span className="mt-0.5 block truncate text-[11.5px] text-neutral-400">
                            {scenario.levers.filter((l) => l.cost).map((l) => l.cost).join(' · ')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-500">{scenario.levers.filter((l) => l.enabled).length}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{formatIndex(scenario.result?.after?.index ?? 0)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        <Pill tone={stats.indexDelta > 0 ? 'green' : stats.indexDelta < 0 ? 'red' : 'neutral'}>
                          {formatIndex(stats.indexDelta)}
                        </Pill>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-neutral-500">
                        +{stats.gained} / −{stats.lost}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr] lg:items-start">
          {/* ---------- the editor ---------- */}
          <Card padded className="space-y-3">
            <Field label="Name">
              <Input
                value={draft.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="Raise resettlement compensation"
                aria-label="Scenario name"
              />
            </Field>
            <Field label="What it is">
              <Textarea
                value={draft.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="One or two sentences"
                aria-label="Scenario description"
                rows={2}
              />
            </Field>

            <Field label="Propagation" hint={propagationWords(draft.propagation)}>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={draft.propagation}
                  onChange={(e) => patch({ propagation: Number(e.target.value) })}
                  aria-label="Propagation"
                  className="h-9 flex-1 accent-primary-600"
                />
                <span className="w-8 text-right text-[12px] tabular-nums text-neutral-500">
                  {Math.round(draft.propagation * 100)}%
                </span>
              </div>
            </Field>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[13px] text-ink">Levers</p>
                <p className="text-[11.5px] text-neutral-400">{activeLevers(draft).length} in play</p>
              </div>
              <LeverList
                levers={draft.levers}
                nodes={model.nodes}
                categories={model.categories}
                ineffective={projection?.ineffective || []}
                onChange={(levers) => patch({ levers })}
              />
            </div>

            <Field label="Assumptions" hint="Anything a reader should know before trusting this">
              <Textarea
                value={draft.assumptions}
                onChange={(e) => patch({ assumptions: e.target.value })}
                placeholder="e.g. assumes the ministry holds its current line"
                aria-label="Assumptions"
                rows={2}
              />
            </Field>

            <div className="flex flex-wrap items-center gap-2 border-t border-neutral-200/70 pt-3">
              <Button size="sm" variant="primary" onClick={run} loading={saving} disabled={!isProjectable(draft)}>
                <Play className="h-4 w-4" strokeWidth={2} />
                Run
              </Button>
              <Button size="sm" variant="secondary" onClick={save} loading={saving} disabled={!dirty || !draft.name.trim()}>
                <Save className="h-4 w-4" strokeWidth={1.75} />
                Save
              </Button>
              {draft.id && (
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                  Delete
                </Button>
              )}
              {previewing && (
                <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-neutral-400">
                  <Spinner />
                  Previewing
                </span>
              )}
            </div>

            {error && <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>}
          </Card>

          {/* ---------- the answer ---------- */}
          <div>
            {!isProjectable(draft) ? (
              <Card>
                <EmptyState
                  icon={FlaskConical}
                  title={
                    draft.levers.length === 0
                      ? 'Add a lever — something we could do, and who it moves'
                      : draft.levers.every((lever) => !lever.enabled)
                        ? 'Every lever is switched off — turn one on to see the projection'
                        : 'Name who each lever moves to see the projection'
                  }
                />
              </Card>
            ) : (
              <>
                {summary.moved > 0 && (
                  <p className="mb-2 text-[11.5px] text-neutral-400">
                    {dirty || !draft.id
                      ? 'Previewed live as you edit · Run to save this projection against the scenario'
                      : `Saved against ${draft.name || 'this scenario'}`}
                  </p>
                )}
                <ProjectionView
                  projection={projection}
                  pending={previewing}
                  propagation={draft.propagation}
                  assumptions={draft.assumptions}
                  reducedMotion={reducedMotion}
                />
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} label="Delete scenario" className="sm:max-w-sm">
        <DialogHeader title="Delete this scenario?" onClose={() => setConfirmDelete(false)} />
        <DialogBody>
          <p className="text-[13px] text-neutral-600">
            {draft.name || 'This scenario'} and its stored projection will be removed. This cannot be undone.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={remove}>
            Delete
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default ScenarioLab;
